import { useState } from 'react';
import {
  Button,
  DataTable,
  FilterBar,
  FilterField,
  Input,
  KpiCard,
  KpiGrid,
  PageShell,
  Pagination,
  RadixSelect,
  Skeleton,
  StatusBadge,
  type DataTableColumn,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { labelOf, MILEAGE_TX_TYPE_LABELS } from '@/lib/enumLabels';
import { UserSearchSelect } from '@/features/users/components/UserSearchSelect';
import { useUserDetailQuery } from '@/features/users/hooks/useUserDetailQuery';
import { useUserIdentitiesQuery } from '@/features/cms/hooks/useUserIdentitiesQuery';
import { isOverseasMallMember } from '../lib/mileageMallPolicy';

/** 해외몰 회원 지급/차감 차단 안내 — 버튼 tooltip 과 배너에서 같은 문구를 쓴다. */
const OVERSEAS_MILEAGE_NOTICE =
  '해외몰 회원은 마일리지를 사용할 수 없어 지급·차감할 수 없습니다. (마일리지는 한국몰 전용)';
import {
  useMileageBalanceQuery,
  useMileageExpiringQuery,
  useMileageTransactionsQuery,
  useAdminMileageAdjustMutation,
} from '../hooks/useAdminMileageQuery';
import { ManualAdjustDialog, type AdjustDirection } from '../components/ManualAdjustDialog';
import { BulkAdjustModal } from '../components/BulkAdjustModal';
import { MILEAGE_WRITE_ROLES } from '../lib/mileageActionRbac';
import type {
  BulkAdjustResult,
  MileageEarnLot,
  MileageTransaction,
  MileageTransactionType,
} from '../types';

const PAGE_SIZE = 20;
const ALL = '';

// 필터 옵션 라벨은 테이블 셀과 동일한 정본 라벨맵(MILEAGE_TX_TYPE_LABELS)에서
// 도출한다 — 내부 enum 코드(EARN/USE 등)를 사용자에게 노출하지 않는다.
const TYPE_OPTIONS = [
  { value: ALL, label: '전체 유형' },
  ...(['EARN', 'USE', 'EXPIRE', 'ADJUST'] as const).map((type) => ({
    value: type,
    label: MILEAGE_TX_TYPE_LABELS[type] ?? type,
  })),
];

const TYPE_VARIANT: Record<MileageTransactionType, 'success' | 'info' | 'neutral' | 'warning'> = {
  EARN: 'success',
  USE: 'info',
  EXPIRE: 'neutral',
  ADJUST: 'warning',
};

const formatDateTime = (iso: string): string => new Date(iso).toLocaleString('ko-KR');

/**
 * 적립 사유(출처 코드) → 표시 문구(QA-322, BE 회신 2026-10-14). 이관분도 이관팀이 고도몰 사유에 맞는 코드로
 * 정정한다(LEGACY 는 정정 전 데이터). 목록에 없는 값(관리자 지급 시 입력한 사유 등)은 그대로 보인다.
 */
const LOT_REASON_LABELS: Readonly<Record<string, string>> = {
  ORDER_EARN: '상품구매 시 마일리지 지급',
  ORDER_RESTORE: '취소·환불 시 사용 마일리지 환불',
  SIGNUP: '신규회원 가입',
  LEGACY: '고도몰 이관 (정정 전 데이터)',
};
const lotReasonLabel = (reason: string | null): string => (reason ? (LOT_REASON_LABELS[reason] ?? reason) : '-');
const formatSigned = (n: number): string => `${n > 0 ? '+' : ''}${n.toLocaleString()}`;

export const MileageAdminPage = () => {
  const canWrite = useHasRole(MILEAGE_WRITE_ROLES);

  const [userId, setUserId] = useState<string | null>(null);

  /**
   * 마일리지는 한국몰 전용이라 해외몰 회원은 지급/차감이 거부된다(BE 게이트). 여기서 먼저 막아
   * 운영자가 눌러보고 API 오류를 받는 대신 이유를 미리 보게 한다. unmask=false — 몰 판정에
   * 개인정보 원문이 필요 없으므로 마스킹 응답으로 충분하다.
   *
   * 일괄(CSV) 지급은 회원별 몰을 화면이 알 수 없어 여기서 막지 못한다 — 그 경로는 BE 게이트가
   * 회원별 실패로 집계한다.
   */
  const memberDetail = useUserDetailQuery(userId ?? '', false);
  const overseasMember = isOverseasMallMember(memberDetail.data?.mallCode);

  const [draftType, setDraftType] = useState(ALL);
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [applied, setApplied] = useState({ type: ALL, from: '', to: '', page: 1 });

  const [expiringDays, setExpiringDays] = useState(30);
  const [expiringPage, setExpiringPage] = useState(1);

  const [manualDir, setManualDir] = useState<AdjustDirection | null>(null);
  const [bulkDir, setBulkDir] = useState<AdjustDirection | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkAdjustResult | null>(null);

  const balanceQuery = useMileageBalanceQuery(userId);
  const txQuery = useMileageTransactionsQuery(
    {
      ...(userId ? { userId } : {}),
      ...(applied.type ? { type: applied.type as MileageTransactionType } : {}),
      ...(applied.from ? { occurredFrom: applied.from } : {}),
      ...(applied.to ? { occurredTo: applied.to } : {}),
      page: applied.page,
      size: PAGE_SIZE,
    },
    Boolean(userId),
  );
  const expiringQuery = useMileageExpiringQuery({ days: expiringDays, page: expiringPage, size: PAGE_SIZE });
  // 만료 예정 목록은 전체 회원 대상이라 회원을 알아볼 수 있게 표시명(마스킹)을 일괄 조회한다.
  const expiringIdentities = useUserIdentitiesQuery((expiringQuery.data?.items ?? []).map((l) => l.userId));
  const adjust = useAdminMileageAdjustMutation();

  const applyFilters = () => setApplied({ type: draftType, from: draftFrom, to: draftTo, page: 1 });

  const txColumns: DataTableColumn<MileageTransaction>[] = [
    {
      key: 'occurredAt',
      header: '일시',
      width: 170,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.occurredAt)}
        </span>
      ),
    },
    {
      key: 'type',
      header: '유형',
      width: 110,
      cell: (row) => (
        <StatusBadge variant={TYPE_VARIANT[row.type] ?? 'neutral'} label={labelOf(MILEAGE_TX_TYPE_LABELS, row.type)} />
      ),
    },
    {
      key: 'amount',
      header: '금액',
      align: 'right',
      width: 120,
      cell: (row) => (
        <span
          className="text-sm font-medium tabular-nums"
          style={{ color: row.amount < 0 ? 'var(--status-danger-fg)' : 'var(--content-strong)' }}
        >
          {formatSigned(row.amount)}
        </span>
      ),
    },
    {
      key: 'description',
      header: '설명',
      cell: (row) => (
        <span className="block max-w-[280px] truncate text-sm" style={{ color: 'var(--content-muted)' }} title={row.description ?? ''}>
          {row.description || row.reasonMemo || '-'}
        </span>
      ),
    },
  ];

  const expiringColumns: DataTableColumn<MileageEarnLot>[] = [
    {
      key: 'member',
      header: '회원',
      width: 220,
      cell: (row) => {
        const who = expiringIdentities.data?.get(row.userId);
        return (
          <button
            type="button"
            className="flex flex-col text-left hover:underline"
            onClick={() => {
              setUserId(row.userId);
              setApplied((prev) => ({ ...prev, page: 1 }));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            title="이 회원의 잔액·거래내역 보기"
            data-testid="mileage-expiring-member"
          >
            <span className="text-sm" style={{ color: 'var(--content-strong)' }}>{who?.maskedName || '—'}</span>
            {who?.maskedEmail ? (
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{who.maskedEmail}</span>
            ) : null}
          </button>
        );
      },
    },
    {
      key: 'validTo',
      header: '만료일',
      width: 120,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.validTo}
        </span>
      ),
    },
    {
      key: 'earnedAmount',
      header: '적립액',
      align: 'right',
      width: 110,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.earnedAmount.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'remainingAmount',
      header: '만료 예정(잔여)',
      align: 'right',
      width: 120,
      cell: (row) => (
        <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--content-strong)' }}>
          {row.remainingAmount.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'reason',
      header: '사유',
      cell: (row) => (
        <span className="block max-w-[260px] truncate text-sm" style={{ color: 'var(--content-muted)' }} title={row.reason ?? ''}>
          {lotReasonLabel(row.reason)}
        </span>
      ),
    },
  ];

  const txPagination = txQuery.data?.pagination;
  const expiringPagination = expiringQuery.data?.pagination;

  const manualBusy = adjust.grant.isPending || adjust.deduct.isPending;
  const bulkBusy = adjust.bulkGrant.isPending || adjust.bulkDeduct.isPending;

  return (
    <PageShell title="마일리지 관리" description="회원을 검색해 잔액·거래내역을 조회하고 수동/일괄 지급·차감합니다.">
      {overseasMember ? (
        <div
          role="status"
          className="mb-3 rounded-md p-3 text-sm"
          style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
        >
          {OVERSEAS_MILEAGE_NOTICE}
        </div>
      ) : null}
      <FilterBar
        className="mb-4"
        filters={
          <FilterField label="회원" className="col-span-full">
            {/* QA-420 — 필터 그리드 한 칸(약 200px)에 갇혀 검색창이 좁았다 — 한 줄을 다 쓰고 480px 로 고정. */}
            <div className="w-[480px] max-w-[calc(100vw-4rem)]">
            <UserSearchSelect
              value={userId}
              onChange={(id) => {
                setUserId(id);
                setApplied((prev) => ({ ...prev, page: 1 }));
              }}
              testId="mileage-user-select"
              ariaLabel="회원 검색"
            />
            </div>
          </FilterField>
        }
        actions={
          canWrite ? (
            <>
              <Button
                size="sm"
                disabled={!userId || overseasMember}
                title={overseasMember ? OVERSEAS_MILEAGE_NOTICE : undefined}
                onClick={() => setManualDir('grant')}
              >
                수동 지급
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={!userId || overseasMember}
                title={overseasMember ? OVERSEAS_MILEAGE_NOTICE : undefined}
                onClick={() => setManualDir('deduct')}
              >
                수동 차감
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setBulkResult(null); setBulkDir('grant'); }}>
                일괄 지급
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setBulkResult(null); setBulkDir('deduct'); }}>
                일괄 차감
              </Button>
            </>
          ) : undefined
        }
      />

      {userId ? (
        <KpiGrid columns={3} className="mb-4">
          <KpiCard
            density="compact"
            label="현재 마일리지 잔액"
            value={balanceQuery.isPending ? '...' : `${(balanceQuery.data?.balance ?? 0).toLocaleString()}P`}
          />
        </KpiGrid>
      ) : (
        <p className="mb-4 rounded-md p-4 text-sm" style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}>
          회원을 선택하면 잔액과 거래내역이 표시됩니다.
        </p>
      )}

      {userId ? (
        <>
          <FilterBar
            className="mb-3"
            filters={
              <>
                <FilterField label="유형">
                  <RadixSelect value={draftType} onChange={setDraftType} options={TYPE_OPTIONS} aria-label="유형" />
                </FilterField>
                <FilterField label="시작일">
                  <Input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} aria-label="시작일" />
                </FilterField>
                <FilterField label="종료일">
                  <Input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} aria-label="종료일" />
                </FilterField>
              </>
            }
            actions={
              <Button size="sm" onClick={applyFilters}>
                조회
              </Button>
            }
          />

          {txQuery.isPending ? (
            <div role="status" className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-[40px] w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={txColumns}
              rows={[...(txQuery.data?.items ?? [])]}
              rowKey={(row) => row.id}
              emptyState="거래내역이 없습니다."
              stickyHeader
            />
          )}

          {txPagination ? (
            <Pagination
              total={txPagination.totalItems}
              currentPage={txPagination.page}
              pageSize={PAGE_SIZE}
              onPageChange={(p) => setApplied((prev) => ({ ...prev, page: p }))}
              className="mt-3"
            />
          ) : null}
        </>
      ) : null}

      <section className="mt-8" data-testid="mileage-expiring">
        <h2 className="text-[15px] font-semibold" style={{ color: 'var(--content-strong)' }}>
          만료 예정 마일리지 (전체 회원)
        </h2>
        <p className="mb-3 mt-1 text-[13px]" style={{ color: 'var(--content-muted)' }}>
          아래 기간 안에 유효기간이 끝나는 적립분입니다. 회원을 누르면 위에서 그 회원의 잔액·거래내역을 조회합니다.
        </p>
        <FilterBar
          className="mb-3"
          filters={
            <FilterField label="만료 기간 (일 이내)" labelWidth={120}>
              <Input
                type="number"
                min={1}
                value={String(expiringDays)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setExpiringDays(Number.isFinite(v) && v > 0 ? Math.floor(v) : 30);
                  setExpiringPage(1);
                }}
                aria-label="만료 임박 일수"
                className="max-w-[120px]"
              />
            </FilterField>
          }
        />
        {expiringQuery.isPending ? (
          <div role="status" className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[40px] w-full" />
            ))}
          </div>
        ) : (
          <DataTable
            columns={expiringColumns}
            rows={[...(expiringQuery.data?.items ?? [])]}
            rowKey={(row) => row.id}
            emptyState="기간 안에 만료되는 마일리지가 없습니다."
            stickyHeader
          />
        )}
        {expiringPagination ? (
          <Pagination
            total={expiringPagination.totalItems}
            currentPage={expiringPagination.page}
            pageSize={PAGE_SIZE}
            onPageChange={setExpiringPage}
            className="mt-3"
          />
        ) : null}
      </section>

      {manualDir && userId ? (
        <ManualAdjustDialog
          open
          onOpenChange={(o) => { if (!o) setManualDir(null); }}
          direction={manualDir}
          variant="mileage"
          busy={manualBusy}
          memberLabel="선택된 회원"
          onSubmit={({ amount, reason, memo }) => {
            const payload = { userId, amount, reason, ...(memo ? { memo } : {}) };
            const m = manualDir === 'grant' ? adjust.grant : adjust.deduct;
            m.mutate(payload, { onSuccess: () => setManualDir(null) });
          }}
        />
      ) : null}

      {bulkDir ? (
        <BulkAdjustModal
          open
          onOpenChange={(o) => { if (!o) setBulkDir(null); }}
          direction={bulkDir}
          busy={bulkBusy}
          result={bulkResult}
          onSubmit={({ userIds, amount, reason, memo }) => {
            const payload = { userIds, amount, reason, ...(memo ? { memo } : {}) };
            const m = bulkDir === 'grant' ? adjust.bulkGrant : adjust.bulkDeduct;
            m.mutate(payload, { onSuccess: (res) => setBulkResult(res) });
          }}
        />
      ) : null}
    </PageShell>
  );
};
